import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EpiContext from '@episerver/spa-core/Spa';
import Config from '../../../../../Config';
import type ProblemDomainSelectionBlockData from '../../../../../Models/Content/ProblemDomainSelectionBlockData';
import { Onboarding } from '../../../../Page/SelfCarePage/SelfCareAsyncThunk';
import { hideAppLoader, showAppLoader } from '../../../../Shared/Loader/LoaderSlice';
import { iconBackChevron, iconDomainRowChevron } from '../../images';
import NoCustomerDomainsPage from '../NoCustomerDomainsPage/NoCustomerDomainsPage';
import {
  getProblemDomainGreetingName,
  hasSubscriptionsForProductWorld,
  isSubscribersByProductLoaded,
  normalizeProductWorldFromCms,
} from '../../problemDomainHelpers';
import './domainSelectionPage.scss';
import { reportDWH } from '../../problemDomainBlockSlice';

type CmsImageRef = {
  expandedValue?: { url?: string };
  value?: { url?: string };
};

type ProblemDomainItem = {
  domainTitle?: { value?: string };
  domainSelectionIcon?: CmsImageRef;
  productWorld?: { value?: unknown } | unknown;
  contentLink?: { id?: number | string };
};

const mediaUrl = (imageProp: CmsImageRef | undefined): string | null => {
  if (!imageProp) {
    return null;
  }
  const path = imageProp.expandedValue?.url || imageProp.value?.url;
  return path ? Config.epiBaseUrl + path : null;
};

const stripHtmlTags = (html: string): string => html.replace(/<[^>]+>/g, '');

const splitHeadingIntoLines = (content: string): string[] =>
  content
    .replace(/<br\s*\/?>/gi, '\n')
    .split('\n')
    .map((line) => stripHtmlTags(line).trim())
    .filter((line) => line.length > 0);
type OwnProps = {
  iContent: ProblemDomainSelectionBlockData;
  onContinueToFaultFlow: (productWorld: number, domainContentLinkId?: number | string) => void;
};

const DomainSelectionPageView = ({ iContent, onContinueToFaultFlow }: OwnProps): React.ReactElement => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(reportDWH({
      actionType: 'ProductChoosingPage',
      status: 'Ongoing',
      messageText: 'Product Choosing Page Displayed'
    }));
  }, []);

  const subscribersByProduct = useSelector(
    (state: {
      selfcarePage?: { subscribersByProduct?: Record<string, Record<string, unknown>[]> };
    }) => state.selfcarePage?.subscribersByProduct,
  );
  const isFetchLoginData = useSelector((state: { selfcarePage?: { isFetchLoginData?: boolean } }) =>
    Boolean(state.selfcarePage?.isFetchLoginData),
  );
  const loginData = useSelector(
    (state: {
      authentication?: {
        loginData?: {
          isFullAuthenticated?: boolean;
          isOtpAuthenticated?: boolean;
          isOtpExtendedAuthenticated?: boolean;
          isSilentOtpAuthenticated?: boolean;
          userDetails?: {
            firstName?: string;
            contactNumber?: string;
          };
        };
      };
    }) => state.authentication?.loginData,
  );

  const isUserAuthenticated = Boolean(
    loginData?.isFullAuthenticated ||
    loginData?.isOtpAuthenticated ||
    loginData?.isOtpExtendedAuthenticated ||
    loginData?.isSilentOtpAuthenticated,
  );

  const productsLoaded = isSubscribersByProductLoaded(subscribersByProduct);

  useEffect(() => {
    if (!isUserAuthenticated && !isFetchLoginData) {
      return;
    }
    if (productsLoaded) {
      return;
    }
    dispatch(Onboarding());
  }, [dispatch, isUserAuthenticated, isFetchLoginData, productsLoaded]);

  const isLoadingDomains = (isUserAuthenticated || isFetchLoginData) && !productsLoaded;

  useEffect(() => {
    if (!isLoadingDomains) {
      return;
    }

    dispatch(showAppLoader(undefined));

    return () => {
      dispatch(hideAppLoader(undefined));
    };
  }, [dispatch, isLoadingDomains]);

  const headingTemplate = iContent?.heading?.value;
  const headingLines = useMemo((): string[] | null => {
    const template = headingTemplate ?? '';
    if (!template.trim()) {
      return null;
    }

    const greetingName = getProblemDomainGreetingName(loginData?.userDetails, undefined);
    if (template.trim().startsWith('היי')) {
      const text = greetingName
        ? template.replace(/^היי(?:\s[^,<]*)?,/, `היי ${greetingName},`)
        : template;
      const lines = splitHeadingIntoLines(text);
      return lines.length > 0 ? lines : null;
    }

    const greeting = greetingName ? `היי ${greetingName},` : 'היי,';
    const lines = [greeting, ...splitHeadingIntoLines(template)];
    return lines.length > 0 ? lines : null;
  }, [headingTemplate, loginData]);
  const bgDesktop = mediaUrl(iContent?.backgroundImageDesktop);
  const bgMobile = mediaUrl(iContent?.backgroundImageMobile);
  const items = useMemo(
    () => (iContent?.domainItems?.expandedValue || []) as unknown as ProblemDomainItem[],
    [iContent?.domainItems?.expandedValue],
  );

  const visibleDomainItems = useMemo(() => {
    if (!productsLoaded) {
      return [];
    }
    return items.filter((item) =>
      hasSubscriptionsForProductWorld(subscribersByProduct, item.productWorld),
    );
  }, [items, subscribersByProduct, productsLoaded]);

  const sectionStyle = {
    ...(bgDesktop ? { '--prob-domain-bg-desktop': `url("${bgDesktop}")` } : {}),
    ...(bgMobile ? { '--prob-domain-bg-mobile': `url("${bgMobile}")` } : {}),
  } as React.CSSProperties;
  const shouldShowNoCustomerDomains =
    !isLoadingDomains &&
    ((!isUserAuthenticated && !isFetchLoginData) ||
      (productsLoaded && visibleDomainItems.length === 0));

  const handleDomainActivate = (item: ProblemDomainItem) => {
    const world = normalizeProductWorldFromCms(item.productWorld);
    if (world == null || world < 0) {
      return;
    }
    onContinueToFaultFlow(world, item.contentLink?.id);
  };

  if (isLoadingDomains) {
    return <section className="problem-domain-selection problem-domain-selection--loading" style={sectionStyle} />;
  }


  return (
    <section
      className={`problem-domain-selection${shouldShowNoCustomerDomains ? ' problem-domain-selection--no-customer-domains' : ''}`}
      style={sectionStyle}
    >
      {!shouldShowNoCustomerDomains ? (
      <header className="problem-domain-selection__top">
        <button
          type="button"
          className="problem-domain-selection__back"
          aria-label='חזרה ללובי עולם שירות ותמיכה'
          onClick={() => window.history.back()}
        >
          <span className="problem-domain-selection__back-content">
            <img
              src={iconBackChevron}
              alt=""
              width={16}
              height={16}
              className="problem-domain-selection__back-icon"
              aria-hidden
            />
            <span>חזרה</span>
          </span>
        </button>
      </header>
      ) : null}
      <div className="problem-domain-selection__shell">
        {headingLines && !shouldShowNoCustomerDomains ? (
          <h1 className="problem-domain-selection__title">
            {headingLines.map((line, index) => (
              <span key={index}>
                {index > 0 ? <br /> : null}
                {line}
              </span>
            ))}
          </h1>
        ) : null}

        {shouldShowNoCustomerDomains ? (
          React.createElement(NoCustomerDomainsPage, { link: iContent?.noCustomerDomainsLink })
        ) : (
          <ul className="problem-domain-selection__list" role="list">
            {visibleDomainItems.map((item, idx) => {
              const title = item.domainTitle?.value;
              const iconUrl = mediaUrl(item.domainSelectionIcon);
              const key = item.contentLink?.id ?? idx;
              const label = title || `תחום ${idx + 1}`;

              return (
                <li key={key} className="problem-domain-selection__item">
                  <button
                    type="button"
                    className="problem-domain-selection__card"
                    aria-label={label}
                    onClick={() => handleDomainActivate(item)}
                  >
                    <span className="problem-domain-selection__card-main">
                      {iconUrl ? (
                        <span className="problem-domain-selection__icon-wrap">
                          <img src={iconUrl} alt="" className="problem-domain-selection__icon" />
                        </span>
                      ) : null}
                      <span className="problem-domain-selection__label">{label}</span>
                    </span>
                    <span className="problem-domain-selection__chevron-btn" aria-hidden>
                      <img
                        src={iconDomainRowChevron}
                        width={9}
                        height={14}
                        className="problem-domain-selection__chevron-img"
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default DomainSelectionPageView;
